import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../lib/api';
import type { InvoiceWindow } from '../lib/invoiceWindow';
import type {
  BulkDeleteInvoicesResult,
  BulkDueDateInvoicesResult,
  BulkMarkPaidResult,
  Invoice,
  InvoiceDetail,
  InvoiceListItem,
} from '@repo/shared-types';
import type {
  BulkDeleteInvoicesInput,
  BulkDueDateInvoicesInput,
  BulkMarkPaidInvoicesInput,
  CreateInvoiceInput,
  UpdateInvoiceInput,
} from '@repo/validation';
import { toast } from 'sonner';

// List rows carry `students` (THI-93's search needs it) but never `items` —
// see InvoiceListItem's doc comment in shared-types. Detail rows carry each
// item's `enrollmentRef` (THI-88) — see invoiceMessage.ts's adapter, the one
// consumer that reads it.
//
// `window` is optional (THI-94): InvoicesPage always passes one (the query
// key includes it, so react-query caches each window separately — picking a
// different month is a fresh fetch, not a client-side re-filter of a fixed
// set). Omitted entirely — not just undefined fields — by callers that need
// the FULL unwindowed history, e.g. LinkPaymentDialog matching a payment
// against any invoice regardless of age; that call keeps working exactly as
// before this feature.
export function useInvoices(window?: InvoiceWindow) {
  return useQuery<InvoiceListItem[]>({
    queryKey: ['invoices', window ?? null],
    queryFn: () => {
      if (!window) return fetchApi('/invoices');
      const params = new URLSearchParams({ from: window.from, to: window.to });
      if (window.strict) params.set('strict', '1');
      return fetchApi(`/invoices?${params.toString()}`);
    },
  });
}

export function useInvoice(id: string | null) {
  return useQuery<InvoiceDetail>({
    queryKey: ['invoices', id],
    queryFn: () => fetchApi(`/invoices/${id}`),
    enabled: id !== null,
  });
}

/** `GET /invoices/:id/qr`. `paymentRef` really is nullable on the wire — the
 *  route returns `invoice.paymentRef` while building the QR from a generated
 *  fallback ref (see its handler) — so a caller must not assume a string. */
export interface InvoiceQr {
  qrUrl: string;
  paymentRef: string | null;
  transferContent: string;
}

/**
 * The VietQR for one invoice, keyed by invoice id so the send dialog, the chat
 * artifact panel and the phone detail block all share one cached result.
 *
 * `retry: false` is load-bearing: the route answers 400 with a `code`
 * (`BANK_NOT_LINKED`, `UNSUPPORTED_BANK`, `NOTHING_TO_COLLECT`) for states that
 * will never succeed on a retry, and the UI branches on that code.
 */
export function useInvoiceQr(invoiceId: string, enabled: boolean) {
  return useQuery<InvoiceQr>({
    queryKey: ['invoices', invoiceId, 'qr'],
    queryFn: () => fetchApi(`/invoices/${invoiceId}/qr`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateInvoiceInput) => 
      fetchApi<Invoice>('/invoices', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create invoice');
    }
  });
}

export function useUpdateInvoice(id: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateInvoiceInput) => 
      fetchApi<Invoice>(`/invoices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update invoice');
    }
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/invoices/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete invoice');
    }
  });
}

// ── Bulk invoice actions (selection bar on the invoices list) ───────────────
// See docs/superpowers/specs/2026-08-17-bulk-invoice-actions-design.md §2-3.
// Success toasts are left to the caller (InvoicesPage): the wording depends
// on the response's `marked`/`deleted` + `skipped` counts, which only the
// caller knows how to phrase — same split as useRecordPayment/useLinkPayment
// in usePayments.ts, where the hook invalidates and the component decides
// what to say. Errors are generic here since a partial/mixed failure never
// happens (the API is all-or-nothing on tenancy, §2-3), so the fallback
// covers the realistic case.

export function useBulkMarkPaidInvoices() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('invoices');
  return useMutation<BulkMarkPaidResult, Error, BulkMarkPaidInvoicesInput>({
    mutationFn: (data) =>
      fetchApi<BulkMarkPaidResult>('/invoices/bulk/mark-paid', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => {
      toast.error(error.message || t('bulk.toast.markPaidError'));
    },
  });
}

export function useBulkDeleteInvoices() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('invoices');
  return useMutation<BulkDeleteInvoicesResult, Error, BulkDeleteInvoicesInput>({
    mutationFn: (data) =>
      fetchApi<BulkDeleteInvoicesResult>('/invoices/bulk/delete', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => {
      toast.error(error.message || t('bulk.toast.deleteError'));
    },
  });
}

// Bulk re-date (THI-100), DRAFT-only — see docs/superpowers/specs/
// 2026-08-19-due-date-control-design.md §4. Same shape/invalidation as the
// two bulk hooks above; success toast is left to the caller (same split).
export function useBulkDueDateInvoices() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('invoices');
  return useMutation<BulkDueDateInvoicesResult, Error, BulkDueDateInvoicesInput>({
    mutationFn: (data) =>
      fetchApi<BulkDueDateInvoicesResult>('/invoices/bulk/due-date', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => {
      toast.error(error.message || t('bulk.toast.dueDateError'));
    },
  });
}

// ── M8-BE Outreach hooks ────────────────────────────────────────────────────

export interface DraftedMessage {
  to: string | null;
  subject: string;
  body: string;
  _fallback?: boolean;
}

export interface SendInvoiceInput {
  to: string;
  subject: string;
  body: string;
}

export function useDraftInvoiceMessage(id: string) {
  return useMutation<DraftedMessage, Error, void>({
    mutationFn: () =>
      fetchApi<DraftedMessage>(`/invoices/${id}/draft-message`, {
        method: 'POST',
      }),
  });
}

export function useSendInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SendInvoiceInput>({
    mutationFn: (input) =>
      fetchApi<void>(`/invoices/${id}/send`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
    },
  });
}

export function useDraftReminder(id: string) {
  return useMutation<DraftedMessage, Error, void>({
    mutationFn: () =>
      fetchApi<DraftedMessage>(`/invoices/${id}/draft-reminder`, {
        method: 'POST',
      }),
  });
}

export function useSendReminder(id: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SendInvoiceInput>({
    mutationFn: (input) =>
      fetchApi<void>(`/invoices/${id}/send-reminder`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
    },
  });
}

// ── Artifact <-> Invoice lookup ─────────────────────────────────────────────

export function useInvoiceByArtifact(threadId: string | null, artifactIndex: number | null) {
  return useQuery<Invoice | null>({
    queryKey: ['invoices', 'by-artifact', threadId, artifactIndex],
    queryFn: () => {
      const params = new URLSearchParams({
        threadId: threadId!,
        artifactIndex: String(artifactIndex),
      });
      return fetchApi<Invoice | null>(`/invoices/by-artifact?${params.toString()}`);
    },
    enabled: threadId !== null && artifactIndex !== null && artifactIndex >= 0,
    staleTime: 15_000,
  });
}

// ── Mark-shared (Zalo / out-of-band) ────────────────────────────────────────

export function useMarkShared(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Invoice, Error, { channel?: 'zalo' | 'sms' | 'other' }>({
    mutationFn: (input) =>
      fetchApi<Invoice>(`/invoices/${id}/mark-shared`, {
        method: 'POST',
        body: JSON.stringify({ channel: input.channel ?? 'zalo' }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices', 'by-artifact'] });
    },
  });
}

// ── Zalo real send (ZBS) ────────────────────────────────────────────────────

export interface SendZaloResult {
  znsMsgId: string;
}

export function useSendZalo() {
  const queryClient = useQueryClient();
  return useMutation<SendZaloResult, Error, string>({
    mutationFn: (invoiceId) =>
      fetchApi<SendZaloResult>(`/invoices/${invoiceId}/send-zalo`, {
        method: 'POST',
      }),
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
    },
  });
}
